/**
 * ManagerOverrideDialog — Lovable-delivered contract re-implemented with
 * Radix Dialog (B1.7 + B1.8 pattern). The original Lovable primitive
 * imports shadcn/ui (`@/components/ui/*`) which is not vendored in
 * web-business; the dialog visuals, props and behavior stay 1:1 with
 * the Lovable brief.
 */
import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import type { DayCloseLabels, ManagerOverrideInput } from './types';

export interface ManagerOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: ManagerOverrideInput) => Promise<void> | void;
  labels: DayCloseLabels;
  isBusy?: boolean;
  pinAttempts?: number;
}

export function ManagerOverrideDialog({
  open,
  onOpenChange,
  onSubmit,
  labels,
  isBusy = false,
  pinAttempts = 0,
}: ManagerOverrideDialogProps) {
  const [pin, setPin] = React.useState('');
  const [reason, setReason] = React.useState<string>('');
  const [explanation, setExplanation] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setPin('');
      setReason('');
      setExplanation('');
      setError(null);
    }
  }, [open]);

  const reasonKeys = Object.keys(labels.override.reasonCodes);
  const requiresExplanation = reason === 'other_explained';
  const valid =
    pin.length === 4 && reason !== '' && (!requiresExplanation || explanation.trim().length >= 10);

  const handleSubmit = async () => {
    if (!valid) return;
    if (pin === '0000') {
      setError(labels.override.errorInvalidPin);
      return;
    }
    setError(null);
    await onSubmit({
      managerPin: pin,
      reasonCode: reason,
      explanation: requiresExplanation ? explanation : null,
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[14px] bg-biz-surface-elevated p-5 biz-shadow-lg focus:outline-none"
          aria-describedby="manager-override-description"
        >
          <header className="space-y-1 mb-4">
            <Dialog.Title className="text-base font-bold font-display text-biz-foreground">
              {labels.override.title}
            </Dialog.Title>
            <Dialog.Description
              id="manager-override-description"
              className="text-sm text-biz-foreground-muted"
            >
              {labels.override.description}
            </Dialog.Description>
          </header>

          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="manager-pin"
                className="text-[10px] font-semibold uppercase tracking-wider text-biz-foreground-muted"
              >
                {labels.override.managerPin}
              </label>
              <input
                id="manager-pin"
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder={labels.override.managerPinPlaceholder}
                className="h-12 w-full rounded-biz-btn border border-biz-border bg-biz-bg px-3 text-center font-mono-num text-2xl tabular-nums tracking-[0.5em] text-biz-foreground biz-focus-ring"
              />
              {error && (
                <p className="text-xs text-biz-danger" role="alert">
                  {error}
                </p>
              )}
              {pinAttempts > 0 && pinAttempts < 3 && (
                <p className="text-xs text-biz-warning">{pinAttempts}/3</p>
              )}
            </div>

            <div className="space-y-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-biz-foreground-muted">
                {labels.override.reasonCode}
              </span>
              <div role="radiogroup" className="flex flex-col gap-2">
                {reasonKeys.map((k) => {
                  const checked = reason === k;
                  return (
                    <label
                      key={k}
                      className={cn(
                        'flex items-center gap-3 rounded-biz-btn border px-3 py-2.5 cursor-pointer transition-colors biz-focus-ring',
                        checked
                          ? 'border-biz-primary bg-biz-primary-subtle text-biz-primary-subtle-fg'
                          : 'border-biz-border hover:bg-biz-muted',
                      )}
                    >
                      <input
                        type="radio"
                        name="manager-override-reason"
                        value={k}
                        checked={checked}
                        onChange={() => setReason(k)}
                        className="h-4 w-4 accent-biz-primary"
                      />
                      <span className="text-sm text-biz-foreground">
                        {labels.override.reasonCodes[k]}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {requiresExplanation && (
              <div className="space-y-2">
                <label
                  htmlFor="explanation"
                  className="text-[10px] font-semibold uppercase tracking-wider text-biz-foreground-muted"
                >
                  {labels.override.explanation}
                </label>
                <textarea
                  id="explanation"
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  placeholder={labels.override.explanationPlaceholder}
                  rows={3}
                  minLength={10}
                  className="w-full rounded-biz-btn border border-biz-border bg-biz-bg p-3 text-sm text-biz-foreground placeholder:text-biz-foreground-subtle biz-focus-ring"
                />
                {explanation.trim().length > 0 && explanation.trim().length < 10 && (
                  <p className="text-xs text-biz-warning">{labels.override.errorExplanation}</p>
                )}
              </div>
            )}
          </div>

          <footer className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-12 rounded-biz-btn border border-biz-border-strong bg-biz-surface-elevated px-4 font-semibold text-biz-foreground biz-focus-ring hover:bg-biz-muted"
            >
              {labels.cta.cancel}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!valid || isBusy}
              className="h-12 rounded-biz-btn bg-biz-primary px-5 font-bold text-biz-primary-foreground biz-focus-ring biz-shadow-md hover:bg-biz-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {labels.override.submit}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
